import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Dropdown } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty, isNil } from 'ramda';
import Spinner from 'components/Shared/Spinner';
import {
  fetchStudentProficiencyReportRequest,
  setReportLevel,
  setSyllabus
} from 'stores/report/actions';
import { fetchUserCurriculumsRequest } from 'stores/curriculums/actions';
import SkillDetails from 'components/Report/SkillDetails';
import StudentReport from 'components/Report/StudentReport';
import ToggleReport from 'components/Report/ToggleReport';
import avatarPlaceholder from 'assets/img/avatar.png';
import PrintIcon from 'assets/img/icon-report-print.svg';
import ArrowIcon from 'assets/img/icon-report-print-arrow.svg';
import { PrintButton } from 'components/Report/TopBar.styles';
import { featureToggle } from 'constants/index';
import {
  Header,
  Content,
  ProficiencySkills,
  StudentReportPage
} from './StudentHighScoreReport.styles';

const StudentProficiencyReport = ({ match }) => {
  const { t } = useTranslation(['proficiency-report']);
  const studentID = parseInt(match.params.studentID, 10);
  const history = useHistory();
  const dispatch = useDispatch();
  // Redux States
  const {
    studentProficiencyReport,
    isLoading,
    selectedClass,
    reportError,
    reportLevel,
    selectedSyllabus
  } = useSelector(state => state.reports);
  const { curriculums, error } = useSelector(state => state.curriculums);
  // Component states
  const [showSkillDetails, setShowSkillDetails] = useState(false);
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [selectedSkillTopic, setSelectedSkillTopic] = useState(null);
  const [selectedSkillNumber, setSelectedSkillNumber] = useState(null);

  // const reportLevels = generateLevels();
  const isLeveless = isEmpty(selectedSyllabus.Levels);
  const levels = !isEmpty(selectedSyllabus.Levels)
    ? selectedSyllabus.Levels
    : [];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getLevel = syll => {
    const isSelectedLeveless = isEmpty(syll.Levels);
    const checkLevelAvailable =
      syll && syll.Levels.find(item => item.ID === reportLevel);
    if (!isSelectedLeveless) {
      if (isNil(checkLevelAvailable)) {
        return syll.Levels[0];
        // eslint-disable-next-line no-else-return
      } else {
        return reportLevel;
      }
      // eslint-disable-next-line no-else-return
    } else {
      return { ID: -1 };
    }
  };

  useEffect(() => {
    if (error !== '') {
      dispatch(fetchUserCurriculumsRequest());
    }
  }, [dispatch, error]);
  useEffect(() => {
    dispatch(
      fetchStudentProficiencyReportRequest({
        studentID,
        classID: selectedClass.ID,
        reportLevel: isLeveless ? -1 : reportLevel.ID,
        syllabusID: selectedSyllabus.CurriculumID
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    reportLevel,
    selectedClass.ID,
    selectedSyllabus,
    selectedSyllabus.CurriculumID,
    studentID
  ]);
  return (
    <StudentReportPage>
      {isLoading && <Spinner />}
      {!isLoading && reportError && (
        <div className="error">
          <h3>{reportError}</h3>
        </div>
      )}
      {!isLoading && studentProficiencyReport && (
        <>
          <Header>
            <span
              className="back-button"
              onClick={() => {
                history.push('/report');
              }}
            >
              {t(`proficiency-report:studentReport.back`, '< Back')}
            </span>
            <span className="student-details">
              <img
                src={studentProficiencyReport.ProfilePictureURL}
                onError={e => {
                  e.target.src = avatarPlaceholder;
                }}
                alt="Student Profile"
                border="0"
                width="50px"
                height="50px"
                style={{
                  marginRight: '0.25rem',
                  borderRadius: '50%'
                }}
              />
              <strong>{studentProficiencyReport.Username}</strong>
            </span>
            <ToggleReport studentID={studentID} />
            {featureToggle.reportPrintButton && (
              <PrintButton>
                <button type="button">
                  <img src={PrintIcon} alt="print" />
                  {t(`proficiency-report:studentReport.print`, 'Print')}{' '}
                  <span>
                    <img src={ArrowIcon} alt="arrow" />
                  </span>
                </button>
              </PrintButton>
            )}
          </Header>
          <Content showSkillDetails={showSkillDetails} type="proficiency">
            <div className="section-container">
              <div className="skills-section">
                <ProficiencySkills proficiency="mastery">
                  <div className="icon" />
                  <span>
                    {t(`proficiency-report:legend.mastery`, 'Mastery')}
                  </span>
                  <span className="prof-descricption">
                    {t(`proficiency-report:legend.proficiency`, 'Proficiency')}{' '}
                    <b>&nbsp;80-100</b>
                  </span>
                </ProficiencySkills>
                <ProficiencySkills proficiency="competent">
                  <div className="icon" />
                  <span>
                    {t(`proficiency-report:legend.competent`, 'Competent')}
                  </span>
                  <span className="prof-descricption">
                    {t(`proficiency-report:legend.proficiency`, 'Proficiency')}{' '}
                    <b>&nbsp;60-79</b>
                  </span>
                </ProficiencySkills>
                <ProficiencySkills proficiency="beginning">
                  <div className="icon" />
                  <span>
                    {t(`proficiency-report:legend.beginning`, 'Beginning')}
                  </span>
                  <span className="prof-descricption">
                    {t(`proficiency-report:legend.proficiency`, 'Proficiency')}{' '}
                    <b>&nbsp;0-59</b>
                  </span>
                </ProficiencySkills>
                <ProficiencySkills proficiency="incomplete">
                  <div className="icon" />
                  <span>
                    {t(`proficiency-report:legend.incomplete`, 'Incomplete')}
                  </span>
                  <span className="prof-descricption">
                    {t(`proficiency-report:legend.question`, 'Question')} &lt;
                    <b>&nbsp;10</b>
                  </span>
                </ProficiencySkills>
              </div>
              <div className="dropdown-section">
                {!isLeveless && (
                  <div className="report-level-dropdown">
                    <p className="dropdown-label">
                      {t(
                        `proficiency-report:header.reportLevel`,
                        'Report Level from'
                      )}
                    </p>
                    <Dropdown flip={false}>
                      <Dropdown.Toggle
                        variant="light"
                        className="dropdown-custom"
                      >
                        {`P${reportLevel.ID}`}
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        {levels.map(level => (
                          <Dropdown.Item
                            key={level.ID}
                            onClick={() => {
                              dispatch(setReportLevel(level));
                              setShowSkillDetails(false);
                            }}
                          >
                            {`P${level.ID}`}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                )}
                <div className="syllabus-dropdown">
                  <p className="dropdown-label">
                    {t(`proficiency-report:header.syllabus`, 'Syllabus')}
                  </p>
                  <Dropdown flip={false}>
                    <Dropdown.Toggle
                      variant="light"
                      className="dropdown-select-syllabus"
                    >
                      <span>{selectedSyllabus.curriculumName}</span>
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      {curriculums.map(syl => (
                        <Dropdown.Item
                          key={syl.ID}
                          onClick={() => {
                            dispatch(setSyllabus(syl));
                            dispatch(setReportLevel(getLevel(syl)));
                            setShowSkillDetails(false);
                          }}
                        >
                          {syl.curriculumName}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </div>
            </div>
            <StudentReport
              setShowSkillDetails={setShowSkillDetails}
              selectedSkill={selectedSkill}
              setSelectedSkill={setSelectedSkill}
              setSelectedSkillTopic={setSelectedSkillTopic}
              setSelectedSkillNumber={setSelectedSkillNumber}
              type="proficiency"
              data={studentProficiencyReport.Topics}
            />
          </Content>
        </>
      )}
      {showSkillDetails && (
        <SkillDetails
          type="proficiency"
          selectedSkill={selectedSkill}
          selectedSkillTopic={selectedSkillTopic}
          selectedSkillNumber={selectedSkillNumber}
        />
      )}
    </StudentReportPage>
  );
};

export default StudentProficiencyReport;
